import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from './ui/dialog';
import { 
  Star, 
  Loader2,
  Trash2,
  Plus,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Star Rating Input Component
const StarRating = ({ value, onChange, label, readonly = false }) => {
  const [hovered, setHovered] = useState(0);
  
  return (
    <div className="space-y-1">
      {label && <Label className="text-xs text-muted-foreground">{label}</Label>}
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={readonly}
            onClick={() => !readonly && onChange(star)}
            onMouseEnter={() => !readonly && setHovered(star)}
            onMouseLeave={() => !readonly && setHovered(0)}
            className={`transition-colors ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
          >
            <Star 
              className={`w-5 h-5 transition-colors ${
                star <= (hovered || value)
                  ? 'text-yellow-500 fill-yellow-500'
                  : 'text-gray-300 dark:text-gray-600'
              }`}
            />
          </button>
        ))}
        {value > 0 && <span className="ml-2 text-sm font-medium">{value}/5</span>}
      </div>
    </div>
  );
};

// Average Stars Display
const AverageStars = ({ rating, showValue = true, size = 'default' }) => {
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);
  const starSize = size === 'small' ? 'w-3 h-3' : 'w-4 h-4';
  
  return (
    <div className="flex items-center gap-1">
      {[...Array(fullStars)].map((_, i) => (
        <Star key={`full-${i}`} className={`${starSize} text-yellow-500 fill-yellow-500`} />
      ))}
      {hasHalf && (
        <div className="relative">
          <Star className={`${starSize} text-gray-300 dark:text-gray-600`} />
          <div className="absolute inset-0 overflow-hidden w-1/2">
            <Star className={`${starSize} text-yellow-500 fill-yellow-500`} />
          </div>
        </div>
      )}
      {[...Array(emptyStars)].map((_, i) => (
        <Star key={`empty-${i}`} className={`${starSize} text-gray-300 dark:text-gray-600`} />
      ))}
      {showValue && rating > 0 && (
        <span className="ml-1 text-sm font-medium">{rating.toFixed(1)}</span>
      )}
    </div>
  );
};

const SupplierRating = ({ supplierId, supplierName, onRatingAdded }) => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [ratings, setRatings] = useState([]);
  const [averages, setAverages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // New rating form state
  const [newRating, setNewRating] = useState({
    quality: 0,
    reliability: 0,
    communication: 0,
    pricing: 0,
    comments: ''
  });

  const fetchRatings = useCallback(async () => {
    if (!supplierId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/suppliers/${supplierId}/ratings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRatings(response.data.ratings);
      setAverages(response.data.averages);
    } catch (error) {
      console.error('Failed to fetch ratings:', error);
    } finally {
      setLoading(false);
    }
  }, [supplierId, token]);

  useEffect(() => {
    if (open) {
      fetchRatings();
    }
  }, [open, fetchRatings]);

  const handleSubmitRating = async () => {
    // Validate all ratings are set
    if (newRating.quality === 0 || newRating.reliability === 0 || 
        newRating.communication === 0 || newRating.pricing === 0) {
      toast.error('Please rate all categories');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/suppliers/${supplierId}/ratings`, newRating, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Rating submitted successfully');
      setNewRating({ quality: 0, reliability: 0, communication: 0, pricing: 0, comments: '' });
      fetchRatings();
      if (onRatingAdded) onRatingAdded();
    } catch (error) {
      console.error('Failed to submit rating:', error);
      toast.error('Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRating = async (ratingId) => {
    if (!confirm('Are you sure you want to delete this rating?')) return;
    
    try {
      await axios.delete(`${API}/suppliers/${supplierId}/ratings/${ratingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rating deleted');
      fetchRatings();
      if (onRatingAdded) onRatingAdded();
    } catch (error) {
      console.error('Failed to delete rating:', error);
      toast.error('Failed to delete rating');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="rating-button">
          <Star className="w-4 h-4 mr-2" />
          Ratings
          {averages?.overall > 0 && (
            <Badge variant="secondary" className="ml-2">
              {averages.overall.toFixed(1)}
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Supplier Ratings
          </DialogTitle>
          <DialogDescription>
            Rate {supplierName} across different categories
          </DialogDescription>
        </DialogHeader>

        {/* Overall Rating Summary */}
        {averages && averages.overall > 0 && (
          <div className="p-4 bg-muted/50 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overall Rating</p>
                <div className="flex items-center gap-2 mt-1">
                  <AverageStars rating={averages.overall} />
                  <span className="text-sm text-muted-foreground">
                    ({ratings.length} {ratings.length === 1 ? 'review' : 'reviews'})
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t">
              {['quality', 'reliability', 'communication', 'pricing'].map((cat) => (
                <div key={cat} className="text-center">
                  <p className="text-xs text-muted-foreground capitalize">{cat}</p>
                  <div className="flex items-center justify-center gap-1 mt-1">
                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                    <span className="text-sm font-medium">{averages[cat]?.toFixed(1) || 0}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add New Rating Form */}
        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-medium flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Rating
          </h4>
          
          <div className="grid grid-cols-2 gap-4">
            <StarRating 
              label="Quality"
              value={newRating.quality}
              onChange={(v) => setNewRating(prev => ({ ...prev, quality: v }))}
            />
            <StarRating 
              label="Reliability"
              value={newRating.reliability}
              onChange={(v) => setNewRating(prev => ({ ...prev, reliability: v }))}
            />
            <StarRating 
              label="Communication"
              value={newRating.communication}
              onChange={(v) => setNewRating(prev => ({ ...prev, communication: v }))}
            />
            <StarRating 
              label="Pricing"
              value={newRating.pricing}
              onChange={(v) => setNewRating(prev => ({ ...prev, pricing: v }))}
            />
          </div>

          <div className="space-y-2">
            <Label>Comments (optional)</Label>
            <Textarea
              placeholder="Add any additional comments about this supplier..."
              value={newRating.comments}
              onChange={(e) => setNewRating(prev => ({ ...prev, comments: e.target.value }))}
              rows={2}
            />
          </div>

          <Button 
            onClick={handleSubmitRating}
            disabled={submitting}
            className="w-full"
            data-testid="submit-rating-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Star className="w-4 h-4 mr-2" />
            )}
            Submit Rating
          </Button>
        </div>

        {/* Existing Ratings */}
        <div className="space-y-3">
          <h4 className="font-medium">Rating History</h4>
          
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : ratings.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No ratings yet. Be the first to rate this supplier!
            </p>
          ) : (
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {ratings.map((rating) => (
                <div 
                  key={rating.rating_id}
                  className="p-3 border rounded-lg space-y-2"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{rating.rated_by_name || 'Admin'}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(rating.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                        <Star className="w-3 h-3 mr-1 fill-current" />
                        {rating.overall}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteRating(rating.rating_id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div className="text-center">
                      <span className="text-muted-foreground">Quality</span>
                      <p className="font-medium">{rating.quality}/5</p>
                    </div>
                    <div className="text-center">
                      <span className="text-muted-foreground">Reliability</span>
                      <p className="font-medium">{rating.reliability}/5</p>
                    </div>
                    <div className="text-center">
                      <span className="text-muted-foreground">Comm.</span>
                      <p className="font-medium">{rating.communication}/5</p>
                    </div>
                    <div className="text-center">
                      <span className="text-muted-foreground">Pricing</span>
                      <p className="font-medium">{rating.pricing}/5</p>
                    </div>
                  </div>
                  
                  {rating.comments && (
                    <p className="text-sm text-muted-foreground italic border-t pt-2 mt-2">
                      "{rating.comments}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { SupplierRating, AverageStars, StarRating };
export default SupplierRating;
